/**
 * Modern Bold · <mb-rug-finder> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * Multi-step rug quiz rendered by sections/modern-rug-finder.liquid. Each
 * step maps its selected options onto Storefront facet params; finishing
 * redirects to the results collection with those params applied.
 *
 * Two facts about Shopify facet URLs drive the whole design (both verified
 * against the live store, both fail silently when got wrong):
 *   • Repeating a key ORs within a facet; different keys AND together.
 *     So one option can carry several values ("Small" = 2'x3' OR 3'x5').
 *   • Taxonomy facets (filter.v.t.*) take an opaque gid, NOT the label.
 *     filter.v.t.shopify.color-pattern=Blue returns zero products;
 *     gid://shopify/TaxonomyValue/2 is the real value. Metafield facets
 *     (filter.p.m.*, filter.v.m.*) do take the plain label.
 * Values are therefore never derived — the merchant supplies them and this
 * file only splits, normalises and encodes.
 *
 * Radio grouping note: an option may override its step's facet param
 * (a "Round" answer under a Size question writes to Shape). Grouping radios
 * by facet param would put such options in a separate group and let the
 * customer pick two answers on a single-select step, so inputs are grouped
 * by a synthetic per-step `name` and carry the real param in data-param.
 *
 * DOM contract (rendered by the section):
 *   [data-mb-rf-intro]       intro screen wrapper (optional)
 *   [data-mb-rf-start]       button that leaves the intro
 *   [data-mb-rf-progress]    progress wrapper
 *   [data-mb-rf-bar]         bar fill, width driven by --mb-rf-progress
 *   [data-mb-rf-count]       aria-live counter, text from data-label-template
 *   [data-mb-rf-back]        back button
 *   [data-mb-rf-step]        one <fieldset> per question
 *     data-select            "single" | "multi"
 *     [data-mb-rf-focus]     tabindex="-1" heading, focus target on entry
 *     input[data-param]      facet param this answer writes to
 *     input[data-values]     "|"-separated facet values (| not "," — real
 *                            style values contain commas: "Traditional, Transitional")
 *   [data-mb-rf-next]        continue button; the last step's is rendered
 *                            with the finish label and triggers the redirect
 *   [data-mb-rf-skip]        skip link
 *   Host attrs: data-results-base
 */

if (!customElements.get('mb-rug-finder')) {
  /** Long enough to see the tick land, short enough not to feel laggy. */
  const ADVANCE_DELAY = 260;

  /** Merchants copy values straight out of a collection URL, so they arrive
   *  percent-encoded as often as raw. Decode first, then re-encode once. */
  const normalizeValue = (raw) => {
    const value = raw.trim();
    if (!value || !value.includes('%')) return value;
    try {
      return decodeURIComponent(value);
    } catch {
      return value; // malformed %-sequence — take it literally
    }
  };

  class MBRugFinder extends HTMLElement {
    #cleanups = [];
    #steps = [];
    #intro = null;
    #progress = null;
    #bar = null;
    #count = null;
    #backBtn = null;
    #current = -1; // -1 = intro
    #pointerDriven = false;
    #advanceTimer = null;
    #booted = false;

    connectedCallback() {
      this.#steps = Array.from(this.querySelectorAll('[data-mb-rf-step]'));
      if (!this.#steps.length) return;

      this.#intro = this.querySelector('[data-mb-rf-intro]');
      this.#progress = this.querySelector('[data-mb-rf-progress]');
      this.#bar = this.querySelector('[data-mb-rf-bar]');
      this.#count = this.querySelector('[data-mb-rf-count]');
      this.#backBtn = this.querySelector('[data-mb-rf-back]');

      this.#wireForm();
      this.#wireModality();
      this.#wireControls();
      this.#wireEditor();

      this.goTo(this.#intro ? -1 : 0);
    }

    disconnectedCallback() {
      clearTimeout(this.#advanceTimer);
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
    }

    #on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.#cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    // ── Wiring ─────────────────────────────────────────────────────────────

    /** The <form> exists for grouping and reset(); submitting it natively
     *  would post the synthetic names, so it never submits. */
    #wireForm() {
      const form = this.querySelector('[data-mb-rf-form]');
      if (!form) return;
      this.#on(form, 'submit', (event) => {
        event.preventDefault();
        this.#advance();
      });
    }

    /** Arrow keys move between radios and fire `change` on every one they
     *  pass. Auto-advancing on that would skip the step on the first arrow
     *  press, so it is gated on the last interaction having been a pointer. */
    #wireModality() {
      this.#on(this, 'pointerdown', () => { this.#pointerDriven = true; }, true);
      this.#on(this, 'keydown', () => { this.#pointerDriven = false; }, true);
    }

    #wireControls() {
      const start = this.querySelector('[data-mb-rf-start]');
      if (start) this.#on(start, 'click', () => this.goTo(0));

      if (this.#backBtn) this.#on(this.#backBtn, 'click', () => this.#back());

      this.#steps.forEach((step, index) => {
        const next = step.querySelector('[data-mb-rf-next]');
        if (next) this.#on(next, 'click', () => this.#advance());

        const skip = step.querySelector('[data-mb-rf-skip]');
        if (skip) {
          this.#on(skip, 'click', () => {
            this.#clearStep(step);
            this.#advance();
          });
        }

        if (step.dataset.select !== 'single') return;

        // `click`, not `change`: re-picking the answer you already chose —
        // routine after stepping Back — changes no value, so `change` never
        // fires and the step would sit there ignoring the tap. Arrow keys can
        // raise `click` too, hence the pointer gate below still does the work
        // of keeping keyboard navigation from skipping a step.
        this.#on(step, 'click', (event) => {
          const input = event.target;
          if (!input.matches('input') || !input.checked) return;
          // Never auto-advance off the last step — that would fire the
          // redirect on a single click, with no chance to review.
          if (!this.#pointerDriven || index >= this.#steps.length - 1) return;
          clearTimeout(this.#advanceTimer);
          this.#advanceTimer = setTimeout(() => this.#advance(), ADVANCE_DELAY);
        });
      });
    }

    /** Without this the merchant can only ever edit step 1 in the theme
     *  editor — selecting a block on any later step shows a hidden fieldset. */
    #wireEditor() {
      if (!window.Shopify?.designMode) return;
      this.#on(document, 'shopify:block:select', (event) => {
        const step = event.target?.closest?.('[data-mb-rf-step]');
        if (!step || !this.contains(step)) return;
        this.goTo(this.#steps.indexOf(step));
      });
    }

    // ── Navigation ─────────────────────────────────────────────────────────

    /** @param {number} index step index, or -1 for the intro screen */
    goTo(index) {
      clearTimeout(this.#advanceTimer);

      const target = index < 0 && !this.#intro ? 0 : index;
      const onIntro = target < 0;
      this.#current = target;

      if (this.#intro) this.#intro.hidden = !onIntro;
      this.#steps.forEach((step, i) => { step.hidden = onIntro || i !== target; });
      if (this.#progress) this.#progress.hidden = onIntro;

      if (onIntro) {
        this.#warm(0);
        this.#reveal(this.#intro, this.#intro?.querySelector('[data-mb-rf-focus]'));
        return;
      }

      this.#paintProgress();
      this.#warm(target + 1);
      // Back is a dead control on the first step when there is no intro.
      if (this.#backBtn) this.#backBtn.hidden = target === 0 && !this.#intro;
      this.#reveal(
        this.#progress || this.#steps[target],
        this.#steps[target].querySelector('[data-mb-rf-focus]'),
      );
    }

    /**
     * Start fetching a step's images before it is shown.
     *
     * Hidden steps never intersect the viewport, so loading="lazy" leaves
     * them unfetched until the step appears — the customer then watches six
     * empty cards fill in. Flipping to eager fetches even inside a
     * display:none subtree, so the next step is ready by the time it opens.
     */
    #warm(index) {
      this.#steps[index]?.querySelectorAll('img[loading="lazy"]')
        .forEach((img) => { img.loading = 'eager'; });
    }

    reset() {
      this.querySelector('[data-mb-rf-form]')?.reset();
      this.goTo(this.#intro ? -1 : 0);
    }

    #advance() {
      if (this.#current >= this.#steps.length - 1) {
        window.location.href = this.buildUrl();
        return;
      }
      this.goTo(this.#current + 1);
    }

    #back() {
      this.goTo(this.#current - 1);
    }

    /**
     * Bring a step into view and hand its heading focus.
     *
     * Steps are not the same height — six colour swatches wrap to two rows,
     * four styles fit on one — so advancing from a tall step to a short one
     * leaves the scroll position pointing past the new question, which then
     * sits above the top of the viewport. Focus makes that worse rather than
     * better: the heading is focused on every step change, and focus landing
     * somewhere off-screen is a failure in its own right.
     *
     * Scrolling and focusing therefore happen together, with preventScroll so
     * the focus call cannot fight the smooth scroll.
     *
     * Both are skipped on the first paint — that runs on page load, where
     * hijacking the scroll position and stealing focus are equally unwanted.
     */
    #reveal(anchor, focusTarget) {
      if (!this.#booted) {
        this.#booted = true;
        return;
      }
      anchor?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
      focusTarget?.focus({ preventScroll: true });
    }

    #paintProgress() {
      const total = this.#steps.length;
      const current = this.#current + 1;

      if (this.#bar) {
        this.#bar.style.setProperty('--mb-rf-progress', `${(current / total) * 100}%`);
      }
      if (this.#count) {
        const template = this.#count.dataset.labelTemplate || '[current] / [total]';
        this.#count.textContent = template
          .replace('[current]', current)
          .replace('[total]', total);
      }
    }

    #clearStep(step) {
      step.querySelectorAll('input:checked').forEach((input) => { input.checked = false; });
    }

    // ── Results URL ────────────────────────────────────────────────────────

    /**
     * Composes the results URL from every checked answer.
     * @returns {string} absolute URL — same shape Shopify's own facet links use
     *   (URLSearchParams matches its encoding: space → "+", "'" → %27).
     */
    buildUrl() {
      const base = this.dataset.resultsBase || '/collections/all';
      const url = new URL(base, window.location.origin);

      this.#steps.forEach((step) => {
        step.querySelectorAll('input:checked').forEach((input) => {
          const param = input.dataset.param;
          if (!param) return;
          (input.dataset.values || '')
            .split('|')
            .map(normalizeValue)
            .filter(Boolean)
            .forEach((value) => url.searchParams.append(param, value));
        });
      });

      return url.toString();
    }
  }

  customElements.define('mb-rug-finder', MBRugFinder);
}
